import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../Translations/LenguageContext";
import ErrorPage from "../../Components/ErrorPage";
import LoadingCubes from "../ViewComponents/LoadingCubes";
import RestoreBackupModal from "./RestoreBackupModal";
import DeleteRecordModal from "../ViewComponents/DeleteRecordModal";
import { Dropdown } from "react-bootstrap";
import { IoOpenOutline } from "react-icons/io5";
import { VscAzure } from "react-icons/vsc";
import { SiGooglecloud } from "react-icons/si";
import { FaAws } from "react-icons/fa";
import EmptyView from "../ViewComponents/EmptyView";

const MainBackup = ({ userData, no_session }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { project_id } = useParams();
  const user_id = sessionStorage.getItem("user_id");
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [dbName, setDbName] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dbType, setDbType] = useState(null);

  const title_empty = "No backup data available";
  const subtitle_empty =
    "All databases are transferred to the backup bucket, where synchronization is handled automatically.";
  const item = "backup";
  const new_item_navigate = `/project_panel/${project_id}/databases/new_database`;

  useEffect(() => {
    if (user_id) {
      fetchRecords();
    } else {
      setError(true);
      setLoading(false);
    }
  }, [user_id]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://backend.erioon.com/get_dbs_deleted",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id }),
        }
      );
      if (response.ok) {
        const result = await response.json();
        const accessData = result.databases || {};
        const filteredData = Object.values(accessData).filter(
          (record) =>
            record.project_id === project_id &&
            record.status !== "deleted_permanently"
        );

        setRecords(filteredData);
        setFilteredRecords(filteredData);
      } else {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredRecords(
      query
        ? records.filter((record) =>
            [
              "_id",
              "project_id",
              "db_name",
              "db_desc",
              "plan",
              "provider",
              "timestamp",
            ].some((field) => record[field]?.toLowerCase().includes(query))
          )
        : records
    );
  };

  const handleCloseModal = () => {
    setShowBackupModal(false);
    setSelectedRecordId(null);
  };

  const handleDeleteClick = (id, name, db_type) => {
    setSelectedRecordId(id);
    setDbName(name);
    setShowDeleteModal(true);
    setDbType(db_type);
  };

  const handleCloseDelete = () => {
    setShowDeleteModal(false);
    setSelectedRecordId(null);
  };

  const handleRestoreDBClick = (id, name) => {
    setShowBackupModal(true);
    setSelectedRecordId(id);
    setDbName(name);
  };

  if (loading) return <LoadingCubes />;
  if (error) return <ErrorPage />;
  if (records.length === 0)
    return (
      <EmptyView
        navigate_link={new_item_navigate}
        title={title_empty}
        subtitle={subtitle_empty}
        item={item}
      />
    );

  const activeRecords = filteredRecords.filter((r) => r.status !== "deleted");
  const deletedRecords = filteredRecords.filter((r) => r.status === "deleted");

  const renderRecordItem = (record) => {
    const isDeleted = record.status === "deleted";
    const Icon =
      record.provider === "azure" ? (
        <VscAzure />
      ) : record.provider === "gc" ? (
        <SiGooglecloud />
      ) : (
        <FaAws />
      );

    return (
      <div
        key={record._id}
        className={
          isDeleted ? "organization_item_deleted" : "organization_item"
        }
      >
        <p
          className={
            isDeleted ? "organization_title_del" : "organization_title"
          }
          onClick={() =>
            !isDeleted &&
            navigate(
              `/project_panel/${project_id}/databases/${record._id}/data`
            )
          }
        >
          {record.db_name} {!isDeleted && <IoOpenOutline />}
        </p>
        <p className="organization_desc">{record.db_desc}</p>

        <div className="organization_footer">
          {isDeleted && (
            <Dropdown>
              <div className="property_btn_action_toggle">
                <Dropdown.Toggle className="custom-dropdown-toggle" as="div">
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu className="custom-dropdown-menu">
                  <>
                    <Dropdown.Item
                      className="drop_down_item activate"
                      onClick={() =>
                        handleRestoreDBClick(record._id, record.db_name)
                      }
                    >
                      <i className="fa-solid fa-check"></i> Restore from backup
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="drop_down_item delete"
                      onClick={() =>
                        handleDeleteClick(
                          record._id,
                          record.db_name,
                          record.db_type
                        )
                      }
                    >
                      <i className="fa-solid fa-trash"></i> Terminate
                      permanently
                    </Dropdown.Item>
                  </>
                </Dropdown.Menu>
              </div>
            </Dropdown>
          )}
          <div className="footer_items_org">
            {isDeleted ? (
              <span className="deleted_span">Deleted</span>
            ) : (
              <i>{Icon}</i>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="main_view_container">
      <div className="work_env_header fade-in">
        <div className="header_title">
          <p className="main_view_title">Backup</p>
          <p className="main_view_subtitle">
            Backups help prevent permanent data loss. <br />
            <span className="main_view_subtitle_warning">
              Once backup data is permanently deleted, it cannot be recovered.
            </span>
          </p>
        </div>
      </div>

      <div className="filter_container fade-in">
        <div className="filter_input_cont">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            className="filter_input"
            placeholder={t.search}
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
      </div>

      <div className="backup_db_main_parent_cont fade-in">
        {activeRecords.length > 0 && (
          <div className="backup_db_parent_cont">
            <p className="section_label_title">Active Databases</p>
            <div className="organizations_cont">
              {activeRecords.map(renderRecordItem)}
            </div>
          </div>
        )}

        {deletedRecords.length > 0 && (
          <div className="backup_db_parent_cont">
            <p className="section_label_title">Deleted Databases</p>
            <div className="organizations_cont">
              {deletedRecords.map(renderRecordItem)}
            </div>
          </div>
        )}
      </div>
      <RestoreBackupModal
        show={showBackupModal}
        handleClose={handleCloseModal}
        selectedRecordId={selectedRecordId}
        fetchRecords={fetchRecords}
        dbName={dbName}
      />

      <DeleteRecordModal
        show={showDeleteModal}
        handleClose={handleCloseDelete}
        recordObject={records.find((r) => r._id === selectedRecordId)}
        fetchRecords={fetchRecords}
        endpoint={"delete_database_backup"}
        record_type={dbName}
        message="This action CANNOT be undone. It will permanently delete the resource and all associated data."
      />
    </div>
  );
};

export default MainBackup;
